import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { packagesAPI } from "@/services/api";
import {
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";
import { Check, Crown, Rocket, Shield, Zap } from "lucide-react";

const tierIcons = {
  starter: Zap,
  growth: Rocket,
  managed: Crown,
  enterprise: Shield,
};

const ClientPackages = () => {
  const [tiers, setTiers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersRes, subRes] = await Promise.all([
        packagesAPI.listTiers(),
        packagesAPI.getSubscription(),
      ]);
      setTiers(tiersRes.data?.tiers || []);
      setSubscription(subRes.data);
    } catch {
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelect = async (tierId) => {
    if (tierId === "enterprise") {
      toast.info("Contact sales for Enterprise pricing");
      return;
    }
    setSelecting(tierId);
    try {
      const res = await packagesAPI.selectTier(tierId);
      setSubscription(res.data);
      toast.success(`Plan updated to ${tierId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update plan");
    } finally {
      setSelecting(null);
    }
  };

  const currentTier = subscription?.tier || "starter";

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        title="Growth Plans"
        description="Paid billing is coming soon. Starter remains active; contact Proovam for early access."
      />

      <TailAdminPanel title="Current plan">
        <div className="flex items-center gap-3">
          <TailAdminStatusBadge tone="primary">{currentTier}</TailAdminStatusBadge>
          <span className="text-sm text-gray-600">
            {subscription?.tier_details?.description || subscription?.features?.length + " features active"}
          </span>
        </div>
      </TailAdminPanel>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {tiers.map((tier) => {
          const Icon = tierIcons[tier.tier_id] || Zap;
          const isCurrent = tier.tier_id === currentTier;
          const billingUnavailable = tier.tier_id !== "starter";
          const price =
            tier.pricing_note ||
            (tier.monthly_fee_ngn === 0 ? "Free" : `₦${tier.monthly_fee_ngn.toLocaleString()}/mo`);

          return (
            <div
              key={tier.tier_id}
              className={`rounded-2xl border p-5 ${
                isCurrent ? "border-brand-400 bg-brand-50/40" : "border-gray-200 bg-white"
              }`}
            >
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-600">
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold text-gray-900">{tier.name}</p>
                  <p className="text-sm text-gray-500">{price}</p>
                </div>
              </div>
              <p className="mb-4 text-sm text-gray-600">{tier.description}</p>
              <ul className="mb-4 space-y-1 text-xs text-gray-600">
                {(tier.features || []).slice(0, 6).map((f) => (
                  <li key={f} className="flex items-start gap-1.5">
                    <Check className="mt-0.5 h-3 w-3 shrink-0 text-brand-500" />
                    {f.replace(/_/g, " ")}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                variant={isCurrent ? "outline" : "default"}
                disabled={isCurrent || billingUnavailable || selecting === tier.tier_id}
                onClick={() => handleSelect(tier.tier_id)}
              >
                {isCurrent ? "Current plan" : billingUnavailable ? "Billing coming soon" : selecting === tier.tier_id ? "Updating..." : "Select plan"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientPackages;
